// Attribution: https://reactjs.org/docs/error-boundaries.html

// Dependencies
import React, { ReactNode } from "react";

// Services
import { alertService } from "@/services/alert";
import { loggerService } from "@/services/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  status?: number;
  message?: string;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Capture error details
    const status = (error as any)?.status as number | undefined;
    const defaultMessage = "Unknown Error Occurred";
    const message = defaultMessage;

    alertService.success(defaultMessage, {
      keepAfterRouteChange: true,
    });

    // Log the error
    loggerService.trackException(error, {
      [message]: errorInfo,
      status,
    });

    // Persist details for render
    this.setState({ status, message });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const displayMessage = "Please reload the website and try again.";
      return (
        <div>
          <h1>{"Unknown Error Occurred."}</h1>
          <h2>{displayMessage}</h2>
          <h3>If issues persist, contact Support.</h3>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
